'use client';

import Typography, { type TypographyProps } from '@mui/material/Typography';
import { animated, useInView, useSpring } from 'react-spring';
import { fNummerMedSpacing } from 'src/utils/format-number';
import { SPRING } from './rörelse-konfig';
import { useRörelse } from './RörelseKontext';

const AnimeradSpan = animated('span');

type Props = {
    till: number;
    från?: number;
    /** Standard är svensk tusentalsavgränsning. Skicka egen för kr, kWh, procent. */
    format?: (n: number) => string;
    suffix?: string;
    fördröjning?: number;
    variant?: TypographyProps['variant'];
    component?: TypographyProps['component'];
    sx?: TypographyProps['sx'];
};

/**
 * Räknare som når sitt slutvärde när den kommer in i vy.
 *
 * `tabular-nums` är inte kosmetik här: utan den ändrar siffrorna bredd för varje bildruta
 * och hela raden runt omkring reflowar medan räkningen pågår.
 */
export function Siffervisare({
    till,
    från = 0,
    format = (n) => fNummerMedSpacing(Math.round(n)),
    suffix,
    fördröjning = 0,
    variant = 'h2',
    component = 'p',
    sx
}: Props) {
    const [ref, synlig] = useInView({ once: true, amount: 0.6 });
    const { mindreRörelse } = useRörelse();

    const { n } = useSpring({
        n: synlig ? till : från,
        from: { n: från },
        config: SPRING.trög,
        delay: mindreRörelse ? 0 : fördröjning,
        immediate: mindreRörelse
    });

    return (
        <Typography
            ref={ref}
            variant={variant}
            component={component}
            sx={{ fontVariantNumeric: 'tabular-nums', ...sx }}
        >
            <AnimeradSpan>{n.to((v) => format(v))}</AnimeradSpan>
            {suffix ? <span>{suffix}</span> : null}
        </Typography>
    );
}
